import React from 'react';
import { FaEdit } from 'react-icons/fa';
import { ExpenseList } from './ExpenseList';

function SummaryCard({ title, value, note, border, color, testId }) {
  return (
    <div className={`bg-white rounded-xl shadow-md p-6 border-l-4 ${border}`}>
      <h4 className="text-sm text-gray-600 mb-2">{title}</h4>
      <p className={`text-3xl font-bold ${color}`} data-testid={testId}>${value.toFixed(2)}</p>
      <p className="text-xs text-gray-500 mt-1">{note}</p>
    </div>
  );
}

function FundingHeader({ availableFunds, budgetGoal, fundingProgress, totalContributions, canManage, onEditGoal }) {
  return (
    <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-xl shadow-md p-6 text-white">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm text-emerald-100 mb-1">Available Funds</h4>
          <p className={`text-4xl font-bold ${availableFunds >= 0 ? 'text-white' : 'text-red-300'}`} data-testid="available-funds">
            ${availableFunds.toFixed(2)}
          </p>
          {availableFunds < 0 && <p className="text-sm text-red-200">Overspent! Need more contributions.</p>}
        </div>
        <div className="text-right">
          <div className="flex items-center justify-end gap-2 mb-1">
            <h4 className="text-sm text-emerald-100">Budget Goal</h4>
            {canManage && (
              <button onClick={onEditGoal} className="p-1.5 rounded-md bg-white/15 hover:bg-white/25 transition-colors" title="Edit budget goal" data-testid="edit-budget-goal-btn">
                <FaEdit className="text-xs" />
              </button>
            )}
          </div>
          <p className="text-3xl font-bold" data-testid="budget-goal-amount">${budgetGoal.toFixed(2)}</p>
          <p className="text-sm text-emerald-200">
            {fundingProgress >= 100 ? 'Goal reached!' : `${fundingProgress.toFixed(0)}% funded`}
          </p>
        </div>
      </div>
      <div className="mt-4">
        <div className="flex justify-between text-sm text-emerald-100 mb-1">
          <span>Contributions: ${totalContributions.toFixed(2)}</span>
          <span>Goal: ${budgetGoal.toFixed(2)}</span>
        </div>
        <div className="w-full bg-emerald-400 rounded-full h-3">
          <div className={`h-3 rounded-full ${fundingProgress >= 100 ? 'bg-green-300' : 'bg-yellow-300'}`} style={{ width: `${Math.min(fundingProgress, 100)}%` }}></div>
        </div>
      </div>
    </div>
  );
}

export function BudgetOverview({ budget, paidExpenses, plannedExpenses, totalContributions, budgetGoal, availableFunds, fundingProgress, canManage, onEditGoal, onAddExpense, onEditExpense, onDeleteExpense, onAddContribution, onContributeOnline }) {
  return (
    <>
      <FundingHeader
        availableFunds={availableFunds}
        budgetGoal={budgetGoal}
        fundingProgress={fundingProgress}
        totalContributions={totalContributions}
        canManage={canManage}
        onEditGoal={onEditGoal}
      />

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <SummaryCard title="Total Contributions" value={totalContributions} note="From members, dues & deposits" border="border-green-500" color="text-green-600" testId="total-contributions" />
        <SummaryCard title="Paid Expenses" value={paidExpenses} note="Already spent" border="border-red-500" color="text-red-600" testId="total-paid-expenses" />
        <SummaryCard title="Planned Expenses" value={plannedExpenses} note="Budgeted, not yet paid" border="border-amber-500" color="text-amber-600" testId="total-planned-expenses" />
        <SummaryCard title="Available to Spend" value={availableFunds} note="Contributions - Paid expenses" border="border-blue-500" color={availableFunds >= 0 ? 'text-blue-600' : 'text-red-600'} testId="available-to-spend" />
      </div>

      <ExpenseList
        expenses={budget.expenses}
        canManage={canManage}
        onAddExpense={onAddExpense}
        onEdit={onEditExpense}
        onDelete={onDeleteExpense}
      />

      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800">Contributions</h3>
          <div className="flex gap-2">
            <button onClick={onAddContribution} className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm" data-testid="add-contribution-btn">
              Record Manual
            </button>
            <button onClick={onContributeOnline} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm" data-testid="contribute-online-btn">
              Contribute Online
            </button>
          </div>
        </div>

        {budget.contributions.length === 0 ? (
          <p className="text-gray-600 text-center py-4">No contributions yet</p>
        ) : (
          <div className="space-y-2">
            {budget.contributions.map((contribution) => (
              <div key={contribution.contribution_id} className="flex items-center justify-between py-2 border-b" data-testid={`contribution-${contribution.contribution_id}`}>
                <p className="font-medium text-gray-800">{contribution.user_name}</p>
                <p className="font-bold text-green-600">${contribution.amount.toFixed(2)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
